import Vue from 'vue';
import store from 'store';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import {
  decode,
  htmlDecode,
  formatPrice,
  getProductImage,
  getImageWithSize,
  slug,
  cleanseProductJson,
  isTablet,
} from 'utils/general';
import { i18n } from 'lib/i18n.js';
import {
  trackProductClick,
  buildItemTrackingData,
  setLocalStorageProperties,
} from 'lib/tracking';
import { trackEvent } from '../../assets/js/lib/tracking';

export default {
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
    'product-card-price-details': () =>
      import('../product-card/product-card-price-details.vue'),
    'bb-product-card-cta-actions': () =>
      import('./bb-product-card-cta-actions.vue'),
  },

  props: [
    'product',
    'selectedItems',
    'limit',
    'stepProducts',
    'hasAvailableProducts',
    'step',
    'parentTitle',
    'cardIndex',
    'parentId',
  ],
  /**
   * Data can come via a JSON string that needs to be
   * decoded, or via an javascript object, passed by a
   * parent component or a Vuex store.
   */
  data() {
    const product = cleanseProductJson(this.product);
    return {
      ...product,
      title: htmlDecode(product.title),
      shortDescription: htmlDecode(product.shortDescription),
      activeOption: '',
      selectedColor: false,
      shadeSelected: false,
      currentShade: null,
      optionMap: { Size: [] },
      hasMultiSwatch: false,
    };
  },
  mounted() {
    this.activeOption = this.firstOption;
    this.buildOptionMap();
  },
  created() {
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  computed: {
    ...mapState('global', ['cart']),
    formattedTitle() {
      // return 'Hydra Vizor Invisible Moisturizer Broad Spectrum Spf 15 Sunscreen Refill '
      return this.title.split(window.__GLOBAL__.productDelimiter)[0];
    },
    variantTitle() {
      return this.title.split(window.__GLOBAL__.productDelimiter)[1];
    },
    formattedPrice() {
      let variants = this.variants.map((variant) => {
        return {
          ...variant,
          is_disabled:
            variant?.bundleBuilderProductIds?.includes(this.parentId) === false,
        };
      });

      let priceToDisplay = null;

      const activeVariants = variants.filter(variant => variant.available && variant.is_disabled === false);

      if (activeVariants.length > 0) {
        const compareAtPrices = activeVariants
          .map(variant => parseInt(variant.compareAtPrice))
          .filter(p => !isNaN(p) && p !== 0); 

        if (compareAtPrices.length > 0) {
          priceToDisplay = Math.min(...compareAtPrices);
        } else {
          priceToDisplay = Math.min(...activeVariants.map(variant => parseInt(variant.price)));
        }

        if (activeVariants.length > 1 && compareAtPrices.length > 0) {
          return `From ${formatPrice(priceToDisplay, true, false)}`;
        }
      } else if (this.variants[0]) {
        priceToDisplay = this.variants[0].compareAtPrice || this.variants[0].price;
      } else {
        priceToDisplay = this.price;
      }

      if (priceToDisplay === null || isNaN(priceToDisplay)) {
          priceToDisplay = 0; 
      }

      return formatPrice(priceToDisplay, true, false);
    },

    formattedDiscountPrice() {
      const availableSaleVariants = this.variants.filter(variant =>
        variant.available && variant.compareAtPrice
      );

      if (availableSaleVariants.length === 0) {
        return false;
      }

      let minAvailableSalePrice = Math.min(
        ...availableSaleVariants.map(variant => parseInt(variant.price))
      );

      if (isNaN(minAvailableSalePrice)) {
          return false; 
      }
      return formatPrice(minAvailableSalePrice, true, false);
    },

    ctaType() {
      if (!this.available) {
        return 'out-of-stock';
      } else if (this.selectedShades.length > 1 || this.sizeCount > 1) {
        return 'quick-shop';
      } else {
        return 'add';
      }
    },
    firstOption() {
      return (this.productOptions || [])[0];
    },
    optionName() {
      return (this.optionNames || [])[0];
    },
    productOptions() {
      return this.optionsWithValues['Size'];
    },
    productDescription() {
      if (
        this.variants[0].shortDescription &&
        this.variants[0].shortDescription !== 'false'
      ) {
        return htmlDecode(this.variants[0].shortDescription);
      }
      if (this.shortDescription && this.shortDescription !== 'false') {
        return this.shortDescription;
      }
      if (
        this.collectionShortDescription &&
        this.collectionShortDescription !== 'false'
      ) {
        return htmlDecode(this.product.collectionShortDescription);
      }
      return false;
    },
    imageSrc() {
      if (this.optionNames.includes('Size')) {
        const sizeVariant = this.variants.find((variant) => {
          return variant.bundleBuilderProductIds?.includes(this.parentId);
        });

        return sizeVariant.image.src;
      }
      return getProductImage('', '', this.images, false, this.featuredImage);
    },
    productId() {
      return this.id;
    },
    hoverImageSrc() {
      if (this.media && this.media[1]) {
        return getImageWithSize(this.media[1].src);
      }
      return this.imageSrc;
    },
    link() {
      return `${Shopify.routes.root}products/${this.handle}${this.activeVariantUrl}`;
    },
    activeVariantUrl() {
      const firstVariant = this.variants.find(({ title = '', id }) => {
        return ~title.indexOf(this.activeOption);
      });
      return firstVariant ? `?variant=${firstVariant.id}` : '';
    },
    activeVariantId() {
      return this.variants[0].id;
    },
    colorKey() {
      return this.optionNames.find((option) =>
        !!option ? option.toLowerCase() === 'color' : null,
      );
    },
    colorOptionIndex() {
      return this.optionNames.indexOf(this.colorKey);
    },
    sizeKey() {
      return this.optionNames.find((option) =>
        !!option ? option.toLowerCase() === 'size' : null,
      );
    },
    sizeOptionIndex() {
      return this.optionNames.indexOf(this.sizeKey);
    },
    swatches() {
      if (!this.colorKey) return false;

      return this.optionsWithValues[this.colorKey];
    },
    optionCombos() {
      return this.variants.map((variant) => {
        const combo = [];
        this.optionNames.forEach((option, i) => {
          combo[i] = slug(variant[`option${i + 1}`]);
        });
        return combo.join(' / ');
      });
    },
    sizeCount() {
      if (this.optionNames.includes('Size')) {
        const sizeVariants = this.variants.filter((variant) => {
          return variant.bundleBuilderProductIds?.includes(this.parentId);
        });

        return sizeVariants.length;
      }
      return 0;
    },
    shadeCountText() {
      if (this.selectedShades && this.selectedShades.length == 1) {
        return this.product.title.split(window.__GLOBAL__.productDelimiter)[1];
      }
      return this.selectedShades && this.selectedShades.length > 1
        ? this.$t('products.card.shade_count', {
            count: this.selectedShades.length,
          })
        : false;
    },
    sizeCountText() {
      if (!this.sizeCount) return false;
      if (this.sizeCount == 1) {
        const sizeVariant = this.variants.find((variant) =>
          variant.bundleBuilderProductIds?.includes(this.parentId),
        );
        const sizeOptionIndex = Object.keys(this.optionsWithValues).findIndex(value => value == 'Size') + 1
        return sizeVariant[`option${sizeOptionIndex}`];
      }
      return this.sizeCount && this.sizeCount > 1
        ? this.$t('products.card.size_count', { count: this.sizeCount })
        : false;
    },
    imageWidth() {
      if (!this.media) return '';
      return this.media?.length ? this.media[0].width : '350';
    },
    imageHeight() {
      if (!this.product.media) return '';
      return this.media?.length ? this.media[0].height : '420';
    },
    bbProductSelected() {
      const isProductSelected = this.selectedItems.find((item) => {
        const title = htmlDecode(
          item.title.split(window.__GLOBAL__.productDelimiter)[0],
        );
        const product_title = htmlDecode(
          this.product.title.split(window.__GLOBAL__.productDelimiter)[0],
        );
        return product_title === title;
      });
      return isProductSelected;
    },
    replaceProduct() {
      return Number(this.limit) === 1;
    },
    listType() {
      return `bundle-step-${this.step + 1}`;
    },
  },
  methods: {
    ...mapActions('cart', [
      'addToCart',
      'openCart',
      'closeCart',
      'addBundleItemsToCart',
    ]),
    ...mapActions('quickView', [
      'setProduct',
      'updateModalTitle',
      'updateCtaText',
      'setGWPModal',
      'updateItemToUpdate',
      'updateAction',
    ]),
    onResize() {
      this.isTablet = isTablet();
    },
    buildOptionMap() {
      Object.keys(this.optionsWithValues).forEach((key, i) => {
        this.optionMap[key] = this.optionsWithValues[key].map((value) => {
          const combo = [];

          this.optionNames.forEach((option, j) => {
            combo[j] = i === j ? slug(value) : this.selectedColor;
          });

          return {
            label: value,
            value: slug(value),
            combo,
            enabled: this.optionCombos.includes(combo.join(' / ')),
          };
        });
      });
    },
    changeActiveOption(value) {
      this.activeOption = value;
    },
    trackSelectItem() {
      const otherData = {
        origin: document.body.dataset.origin,
        originProduct: document.body.dataset.originProduct,
        index: this.cardIndex + 1,
      };

      const listData = {
        listName: this.parentTitle || undefined,
        listId: this.listId,
        index: this.cardIndex + 1,
        componentName: 'Bundle Builder Product Card',
        siteSection: window.__GLOBAL__.siteSection,
        origin: 'bundle builder',
        moduleName: 'Bundle Builder'
      };

      const itemObject = buildItemTrackingData(this.product, 1, otherData);

      trackProductClick('select_item', itemObject, listData);
    },
    async trackClick() {
      this.trackSelectItem();

      setTimeout(() => {
        window.location = this.link;
      }, 800);
    },
    showGallery() {
      trackEvent({
        event: 'product_module_interaction',
        click_action: 'magnifying glass',
        product_name: htmlDecode(this.product.title),
        site_section: window.__GLOBAL__.siteSection,
        component_name: 'Bundle Builder Product Card',
        shopify_module_name: 'bb product card'
      });
      const loadGalleryEvent = new CustomEvent('bb-product-card:gallery', {
        detail: {
          handle: this.product.handle,
        },
      });
      window.dispatchEvent(loadGalleryEvent);
    },
  },
  template: `
    <div class="product-card relative">
      <div
        class="product-card__featured">
        <button @click="showGallery" class="product-card__gallery-button">
          <svg role="img" aria-labelledby="Gallery"  xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
            <title id="Gallery">Open Product Gallery</title>
            <rect width="30" height="30" rx="15" fill="white"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M17.3047 10.6505C19.1419 12.4877 19.1419 15.4665 17.3047 17.3037C15.4674 19.141 12.4887 19.141 10.6515 17.3037C8.81422 15.4665 8.81422 12.4877 10.6515 10.6505C12.4887 8.81324 15.4674 8.81324 17.3047 10.6505ZM18.4434 17.6721C20.3319 15.3953 20.2095 12.0125 18.0761 9.87909C15.8128 7.61582 12.1433 7.61582 9.88007 9.87909C7.6168 12.1424 7.6168 15.8118 9.88007 18.0751C12.0134 20.2084 15.396 20.3309 17.6728 18.4426C17.6784 18.4488 17.6842 18.4549 17.6902 18.4609L20.5829 21.3536C20.7959 21.5666 21.1413 21.5666 21.3543 21.3536C21.5673 21.1406 21.5673 20.7952 21.3543 20.5822L18.4616 17.6895C18.4556 17.6835 18.4496 17.6777 18.4434 17.6721Z" fill="black"/>
          </svg>
        </button>
        <a :href="link" ref="cardLink" class="product-card__featured-image" @click.prevent="trackClick">
          <div is="vue-image-v2"
            class="fit-cover product-card__image"
            :src="imageSrc"
            :alt="formattedTitle + ' view 1'"
            :width="imageWidth"
            :height="imageHeight"
            loading="lazy"
          ></div>
          <div is="vue-image-v2"
            class="fit-cover product-card__image product-card__image--hover"
            :src="hoverImageSrc"
            :alt="formattedTitle + ' view 2'"
            :width="imageWidth"
            :height="imageHeight"
            loading="lazy"
          ></div>
        </a>
      </div>
      <div class="product-card__details">
        <div>
          <p
            class="product-card__details-title p6 bold"
            v-html="formattedTitle">
          </p>
          <p
            v-if="productDescription"
            class="product-card__details-description b2 light"
            v-text="productDescription">
          </p>
          <div class="w1 f fdc">
          <span
            v-if="shadeCountText"
            class="product-card__details__count b2 underline"
            v-html="shadeCountText">
          </span>
          <span
            v-if="sizeCountText"
            class="product-card__details__count b2 underline"
            v-html="sizeCountText">
          </span>
          <span
            v-if="!sizeCountText && !shadeCountText"
            class="product-card__details__variant b2"
            v-text="variantTitle"
          ></span>
        </div>
          <product-card-price-details
            :is-g-w-p="false"
            :formatted-price="formattedPrice"
            :formatted-discount-price="formattedDiscountPrice"
          >
          </product-card-price-details>
        </div>
        <bb-product-card-cta-actions
            :cta-type="ctaType"
            :handle="handle"
            :product="product"
            :variants="variants"
            :parent-title="parentTitle"
            :list-type="listType"
            section-name="Bundle builder"
            :card-index="cardIndex"
            :bundle-item-selected="bbProductSelected"
            :replace-b-b-product="replaceProduct"
            :has-available-products="hasAvailableProducts"
            :step-products="stepProducts"
            :step="step"
            @track-item="trackSelectItem"
            :parent-id="parentId"
          >
          </bb-product-card-cta-actions>
      </div>
  </div>
    `,
};
